import React, { useMemo, useRef, useState } from 'react';
import { css } from '@emotion/core';

import { breakpoints, colors } from '../../../design-system/systemprovider';
import { Dropdown } from '../../../design-system';
import { ReviewsNavigation } from './ReviewsNavigation';
import { useMediaQueries } from '../../common/hooks/useMediaQuery';
import { Feedback } from '../types';
import { heights } from '../../common/constants';

type Props = {
  items: Feedback[];
  pageLimit: number;
  pageNeighbours: number;
  numberOfreviewsLoaded: number;
  renderItem: (item: Feedback) => JSX.Element;
};

export const ReviewsPagination = ({
  items = [],
  pageLimit,
  pageNeighbours,
  numberOfreviewsLoaded,
  renderItem,
}: Props) => {
  const { isMobile, isTablet } = useMediaQueries();
  const [selectedSort, setSelectedSort] = useState<string>('Plus récentes');
  const [currentPage, setCurrentPage] = useState(1);
  const topPaginationRef = useRef<HTMLInputElement>(null);

  const dropdownOptions = [
    { value: 'Plus récentes', label: 'Plus récentes' },
    { value: 'Moins récentes', label: 'Moins récentes' },
    { value: 'Notes croissantes', label: 'Notes croissantes' },
    { value: 'Notes décroissantes', label: 'Notes décroissantes' },
  ];

  const totalCount = items.length;
  const totalPages = Math.ceil(totalCount / pageLimit);

  const gotoPage = (page: number) => {
    if ((isMobile || isTablet) && topPaginationRef.current) {
      topPaginationRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
    const currentPage = Math.max(0, Math.min(page, totalPages));
    setCurrentPage(currentPage);
  };

  const range = (from: number, to: number, step = 1) => {
    let i: number = from;
    const range: number[] = [];

    while (i <= to) {
      range.push(i);
      i += step;
    }

    return range;
  };

  const fetchPageNumbers = () => {
    const totalNumbers = pageNeighbours * 2 + (isMobile ? 2 : 3);
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      let pages: number[];

      const leftBound = currentPage - pageNeighbours;
      const rightBound = currentPage + pageNeighbours;
      const beforeLastPage = totalPages - 1;

      const startPage = leftBound > 2 ? leftBound : 2;
      const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;

      pages = range(startPage, endPage);

      const pagesCount = pages.length;
      const singleSpillOffset = totalNumbers - pagesCount - 1;

      const leftSpill = startPage > 2;
      const rightSpill = endPage < beforeLastPage;

      if (leftSpill && !rightSpill) {
        const extraPages = range(startPage - singleSpillOffset, startPage - 1);
        pages = [...extraPages, ...pages];
      } else if (!leftSpill && rightSpill) {
        const extraPages = range(endPage + 1, endPage + singleSpillOffset);
        pages = [...pages, ...extraPages];
      } else if (leftSpill && rightSpill) {
        pages = [...pages];
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  const customChange = (value: string) => {
    setSelectedSort(value);
    setCurrentPage(1);
  };

  const sortedItems = useMemo(() => {
    switch (selectedSort) {
      case 'Plus récentes':
        return [...items].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'Moins récentes':
        return [...items].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case 'Notes croissantes':
        return [...items].sort((a, b) => a.rating - b.rating);
      case 'Notes décroissantes':
        return [...items].sort((a, b) => b.rating - a.rating);
      default:
        return items;
    }
  }, [items, selectedSort]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageLimit;
    const end = start + numberOfreviewsLoaded;
    return sortedItems.slice(start, end);
  }, [sortedItems, currentPage, numberOfreviewsLoaded]);

  return (
    <>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          border-top: 1px solid ${colors.LIGHT};
          border-bottom: 1px solid ${colors.LIGHT};
          scroll-margin-top: ${heights.HEADER_MOBILE}px;

          @media (min-width: ${breakpoints.S}px) {
            scroll-margin-top: ${heights.HEADER_TABLET}px;
          }

          @media (min-width: ${breakpoints.M}px) {
            scroll-margin-top: initial;
            padding: 0 16px;
          }
        `}
        ref={topPaginationRef}
      >
        <div
          css={css`
            font-size: 1.2rem;
            font-weight: 400;
            color: ${colors.MAIN_GREY};
            align-self: center;
          `}
        >{`${totalCount} commentaire${totalCount > 1 ? 's' : ''}`}</div>
        <div
          css={css`
            display: grid;
            grid-auto-flow: column;
            grid-gap: 4px;
            align-items: center;
            min-width: 185px;
          `}
        >
          <Dropdown
            id="reviews-pdp-selector"
            data-cy="pdp-reviews-dropdown-sorting"
            options={dropdownOptions}
            selected={selectedSort}
            onChange={(value: string) => customChange(value)}
            background="WHITE"
            isReviewsBlock
          />
        </div>
      </div>
      <section>
        {paginatedItems?.map((item, index) => {
          return (
            <article
              key={item.feedback_id}
              id={`pdp-reviews-item-${item.feedback_id}`}
              hidden={index + 1 > pageLimit}
              aria-hidden={index + 1 > pageLimit}
            >
              {renderItem(item)}
            </article>
          );
        })}
      </section>
      <nav
        css={css`
          display: flex;
          width: 100%;
          justify-content: flex-start;
          padding: 16px 0;

          @media (min-width: ${breakpoints.S}px) {
            justify-content: center;
          }

          @media (min-width: ${breakpoints.M}px) {
            padding: 24px 0 0 0;
          }
        `}
      >
        <ReviewsNavigation
          pages={fetchPageNumbers()}
          goToPage={gotoPage}
          currentPage={currentPage}
          totalPages={totalPages}
        />
      </nav>
    </>
  );
};
